// frontend/src/components/dashboard/widgets/OutreachTasksWidget.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetSuccessState } from "./WidgetCard";

const OutreachTasksWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchOutreachTasks = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get("/api/widgets/outreach-tasks", {
          params: {
            only_my_data: onlyMyData,
          },
        });
        setData(response.data || { overdue: [], today: [], upcoming: [], summary: {} });
        setError(false);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching outreach tasks:", error);
        }
        setError(true);
        setData({ overdue: [], today: [], upcoming: [], summary: {} });
      } finally {
        setLoading(false);
      }
    };

    fetchOutreachTasks();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const handleLogOutreach = (task) => {
    // Navigate to outreach page with pre-selected employer and consumer
    const params = new URLSearchParams({ action: "new" });
    if (task.employer_id) params.append("employer_id", task.employer_id);
    if (task.consumer_id) params.append("consumer_id", task.consumer_id);
    navigate(`/crm/placement-outreach?${params.toString()}`);
  };

  const handleViewOutreach = () => {
    navigate("/crm/placement-outreach");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getUrgencyColor = (taskType, daysOverdue) => {
    if (taskType === "overdue") {
      if (daysOverdue > 7) return "danger";
      if (daysOverdue > 3) return "warning";
      return "warning";
    }
    if (taskType === "today") return "info";
    return "secondary";
  };

  const getTaskBadge = (task) => {
    if (task.task_type === "overdue") {
      return (
        <span className={`badge bg-${getUrgencyColor("overdue", task.days_overdue)}`}>
          {task.days_overdue} day{task.days_overdue !== 1 ? "s" : ""} overdue
        </span>
      );
    }
    if (task.task_type === "today") {
      return <span className="badge bg-info">Today</span>;
    }
    return (
      <span className="badge bg-secondary">
        In {task.days_until} day{task.days_until !== 1 ? "s" : ""}
      </span>
    );
  };

  // Combine all tasks for display
  const getAllTasks = () => {
    const all = [
      ...data.overdue.map((t) => ({ ...t, sortOrder: 0 })),
      ...data.today.map((t) => ({ ...t, sortOrder: 1 })),
      ...data.upcoming.map((t) => ({ ...t, sortOrder: 2 })),
    ];
    return all.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return new Date(a.next_followup_date) - new Date(b.next_followup_date);
    });
  };

  const getFilteredTasks = () => {
    switch (activeTab) {
      case "overdue":
        return data.overdue;
      case "today":
        return data.today;
      case "upcoming":
        return data.upcoming;
      default:
        return getAllTasks();
    }
  };

  const tasks = getFilteredTasks();
  const { summary } = data;

  // Show success state when no tasks
  if (!loading && !error && summary.total === 0) {
    return (
      <WidgetCard
        title="Outreach Tasks"
        icon="bi-telephone-outbound"
        iconColor="success"
        loading={loading}
      >
        <WidgetSuccessState message="No pending outreach follow-ups!" />
      </WidgetCard>
    );
  }

  const subtitle = onlyMyData ? "(my outreach only)" : "";

  return (
    <WidgetCard
      title="Outreach Tasks"
      subtitle={!loading && !error ? `Follow-up activities ${subtitle}` : undefined}
      icon="bi-telephone-outbound"
      iconColor={summary.overdue_count > 0 ? "warning" : "primary"}
      loading={loading}
      error={error}
      errorMessage="Unable to load outreach tasks"
      badge={summary.total > 0 ? summary.total : undefined}
      badgeColor={summary.overdue_count > 0 ? "warning" : "primary"}
    >
      {/* Tab filters */}
      <div className="btn-group btn-group-sm mb-3 w-100" role="group">
        <button
          type="button"
          className={`btn ${activeTab === "all" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setActiveTab("all")}
        >
          All ({summary.total})
        </button>
        <button
          type="button"
          className={`btn ${activeTab === "overdue" ? "btn-danger" : "btn-outline-danger"}`}
          onClick={() => setActiveTab("overdue")}
          disabled={summary.overdue_count === 0}
        >
          Overdue ({summary.overdue_count})
        </button>
        <button
          type="button"
          className={`btn ${activeTab === "today" ? "btn-info" : "btn-outline-info"}`}
          onClick={() => setActiveTab("today")}
          disabled={summary.today_count === 0}
        >
          Today ({summary.today_count})
        </button>
        <button
          type="button"
          className={`btn ${activeTab === "upcoming" ? "btn-secondary" : "btn-outline-secondary"}`}
          onClick={() => setActiveTab("upcoming")}
          disabled={summary.upcoming_count === 0}
        >
          Upcoming ({summary.upcoming_count})
        </button>
      </div>

      {/* Tasks list */}
      {tasks.length === 0 ? (
        <div className="text-center text-muted py-3">
          <i className="bi bi-check-circle me-2"></i>
          No tasks in this category
        </div>
      ) : (
        <>
          {/* Desktop view */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-sm table-bordered widget-table mb-0">
                <thead>
                  <tr>
                    <th>Employer</th>
                    <th>Consumer</th>
                    <th>Stage</th>
                    <th>Due</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.slice(0, 10).map((task, index) => (
                    <tr key={`${task.followup_id}-${index}`}>
                      <td className="widget-consumer-name">
                        {task.employer_name || "-"}
                      </td>
                      <td>
                        <small>{task.consumer_name || "General"}</small>
                      </td>
                      <td>
                        <small>{task.stage_name || "-"}</small>
                      </td>
                      <td>
                        {getTaskBadge(task)}
                        <br />
                        <small className="text-muted">{formatDate(task.next_followup_date)}</small>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleLogOutreach(task)}
                          title="Log new outreach"
                        >
                          <i className="bi bi-telephone-plus"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile view */}
          <div className="d-block d-md-none">
            {tasks.slice(0, 8).map((task, index) => (
              <div
                key={`${task.followup_id}-${index}`}
                className={`widget-mobile-card widget-mobile-card--${
                  task.task_type === "overdue" ? "warning" : "info"
                } border bg-light`}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-bold">{task.employer_name || "Unknown Employer"}</div>
                    {task.consumer_name && (
                      <div className="small text-muted">Consumer: {task.consumer_name}</div>
                    )}
                    <div className="small">
                      {getTaskBadge(task)}
                      {task.stage_name && (
                        <span className="ms-2 text-muted">{task.stage_name}</span>
                      )}
                    </div>
                    <div className="small text-muted">
                      Due: {formatDate(task.next_followup_date)}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary ms-2"
                    onClick={() => handleLogOutreach(task)}
                    title="Log new outreach"
                  >
                    <i className="bi bi-telephone-plus"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Footer */}
      {summary.total > 0 && (
        <div className="text-center mt-3">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={handleViewOutreach}
          >
            <i className="bi bi-list-task me-1"></i>
            View All Outreach
          </button>
        </div>
      )}
    </WidgetCard>
  );
};

export default OutreachTasksWidget;